import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dbData from "../../../db.json";

// ✅ Fetch all payments
export const fetchPayments = createAsyncThunk(
  "payment/fetchPayments",
  async () => {
    // Return mock data from db.json
    return dbData.payments || [];
  }
);

// ✅ Fetch single payment by ID
export const fetchPaymentById = createAsyncThunk(
  "payment/fetchPaymentById",
  async (id) => {
    // Find payment by ID from mock data
    const payment = (dbData.payments || []).find(p => p.id === id);
    if (!payment) {
      throw new Error('Payment not found');
    }
    return payment;
  }
);

// ✅ Delete payment (mock implementation)
export const deletePayment = createAsyncThunk(
  "payment/deletePayment",
  async (id) => {
    // Mock delete - just return the id
    return id;
  }
);

// ✅ Update payment (mock implementation)
export const updatePayment = createAsyncThunk(
  "payment/updatePayment",
  async ({ id, data }) => {
    // Mock update - return the data with id
    return { id, ...data };
  }
);

// ✅ Create new payment (mock implementation)
export const createPayment = createAsyncThunk(
  "payment/createPayment",
  async (data) => {
    // Mock create - return the data with a generated id
    return { id: `TXN-${Date.now()}`, ...data };
  }
);

const initialState = {
  items: [],
  detail: null,
  status: "idle",        // list status
  detailStatus: "idle",  // single payment status
  error: null,
};

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchPayments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // Fetch single
      .addCase(fetchPaymentById.pending, (state) => {
        state.detailStatus = "loading";
        state.detail = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.error = action.error.message;
      })

      // Delete
      .addCase(deletePayment.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((p) => p.id !== id);
        if (state.detail?.id === id) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })

      // Update
      .addCase(updatePayment.fulfilled, (state, action) => {
        state.detail = action.payload;
        state.items = state.items.map((p) =>
          p.id === action.payload.id ? action.payload : p
        );
      })

      // Create
      .addCase(createPayment.fulfilled, (state, action) => {
        state.items.push(action.payload);
      });
  },
});

export const { clearDetail } = paymentSlice.actions;
export default paymentSlice.reducer;
